import { useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import TransactionOverlay from '../components/transactions/TransactionOverlay';
import PurchasesManage, { type PurchasesManageSaveReason } from './PurchasesManage';
import type { Bill } from '../types/bill';
import { usePayBillsHub } from './PayBillsHubContext';

type BillOverlayLocationState = {
  returnTo?: string;
};

const PayBillsBillOverlay = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { refetchBills } = usePayBillsHub();
  const routeState = location.state as BillOverlayLocationState | null;

  const parsedId = id != null ? Number(id) : Number.NaN;
  const billId = Number.isNaN(parsedId) || parsedId <= 0 ? undefined : parsedId;

  const close = useCallback(() => {
    const returnTo = routeState?.returnTo;
    if (returnTo) {
      navigate(returnTo, { preventScrollReset: true });
      return;
    }
    navigate('/pay-bills', { preventScrollReset: true });
  }, [navigate, routeState?.returnTo]);

  const handleBillSaved = useCallback(
    (bill: Bill, reason: PurchasesManageSaveReason) => {
      refetchBills();

      if (reason === 'draft_created') {
        navigate(`bill/${bill.id}`, {
          replace: true,
          preventScrollReset: true,
          state: routeState,
        });
        return;
      }

      if (
        reason === 'amended' ||
        reason === 'received' ||
        reason === 'draft_updated' ||
        reason === 'cost_corrected' ||
        reason === 'lines_appended'
      ) {
        close();
      }
    },
    [close, navigate, refetchBills, routeState]
  );

  return (
    <TransactionOverlay headerless variant="wide" onClose={close}>
      <PurchasesManage
        key={billId ?? 'invalid'}
        embedded
        billId={billId}
        onClose={close}
        onBillSaved={handleBillSaved}
      />
    </TransactionOverlay>
  );
};

export default PayBillsBillOverlay;
